import React from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import { ModalFooter } from '@amzn/meridian/modal';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import DataState from '../../../../Types/DataState';
import { GetProxy, TeamProxyRelations } from '../../../../api/Proxy/schema';
import { Profile, TeamProfile } from '../../../../Types';
import useInspireOptions from '../../../../hooks/useInspireOptions';
import useIsRevampedProxyEnabled from '../../../../hooks/useIsRevampedProxyEnabled';
import { ProxyStep } from './StepEnum';
import { findProxyRelationshipData } from './utils';
import useProxy from '../../../../hooks/useProxy';

interface ProxyRelationshipsStepProps {
  teamProxy: GetProxy;
  teamProxyState: (typeof DataState)[keyof typeof DataState];
  loadingProxyIds: Set<string>;
  proxyResponseMessage: string;
  onRemoveOMProxy: (
    proxyUserID: string,
    omUserID: string,
    uniqueId: string,
    proxyAlias: string,
    tmAlias: string
  ) => void;
  onRemoveProxy?: (proxyUserID: string) => void;
  onClose: () => void;
  setStep: (step: ProxyStep) => void;
  setRemoveProxyState: (
    state: (typeof DataState)[keyof typeof DataState]
  ) => void;
  teamProfile: TeamProfile;
  userProfile: Profile;
  cscEntityPrefix: string;
  onAssignProxyForSelf?: () => void;
  showTMProxies?: boolean;
}

export const ProxyRelationshipsStep: React.FC<ProxyRelationshipsStepProps> = ({
  teamProxy,
  teamProxyState,
  loadingProxyIds,
  proxyResponseMessage,
  onRemoveOMProxy,
  onRemoveProxy,
  onClose,
  setStep,
  setRemoveProxyState,
  teamProfile,
  userProfile,
  cscEntityPrefix,
  onAssignProxyForSelf,
  showTMProxies = true,
}) => {
  const { isOm } = useInspireOptions();
  const isRevampedProxyEnabled = useIsRevampedProxyEnabled(userProfile);
  const { isProxying, startProxy, endProxy } = useProxy();
  const navigate = useNavigate();

  const activeProxyRelationships =
    teamProxy?.getTeamProxyV2?.proxyRelations.filter(
      relation => relation.proxyCoachees.length > 0
    ) || [];

  const peopleWhoSetMeAsProxy = userProfile.proxyCoachees || [];

  // Start proxy session function - using the hook
  const startProxySession = (proxyUserId: string, proxyUserName: string) => {
    startProxy({
      proxyUserId,
      proxyUserName,
    });
    onClose();
  };

  // End proxy session function - using the hook
  const endProxySession = () => {
    endProxy();
    onClose();
  };

  // If user is in proxy session, show simplified modal
  if (isProxying) {
    return (
      <Column spacing='400'>
        <Text type='b200'>
          <FormattedMessage
            id='cs_perfportal_proxy_session_active'
            defaultMessage='You are currently in a proxy session. Select End proxy session to manage proxies.'
          />
        </Text>
        <ModalFooter>
          <Row alignmentHorizontal='end' spacing='200' widths='fit'>
            <Button type='primary' onClick={endProxySession}>
              <FormattedMessage
                id='cs_perfportal_end_proxy_session'
                defaultMessage='End proxy session'
              />
            </Button>
            <Button type='secondary' onClick={onClose}>
              <FormattedMessage
                id='cs_perfportal_close'
                defaultMessage='Close'
              />
            </Button>
          </Row>
        </ModalFooter>
      </Column>
    );
  }

  return (
    <Column spacing='400'>
      <Text type='b200'>
        {isOm ? (
          <FormattedMessage
            id='cs_perfportal_proxy_om_description'
            defaultMessage="A proxy manages and coaches another OM or TM's team and has access to that team's metrics and performance data. Proxy assignments start immediately and continue until you come back here and select Remove proxy."
          />
        ) : (
          <FormattedMessage
            id='cs_perfportal_proxy_tm_description'
            defaultMessage="A proxy manages and coaches another TM's team and has access to that team's metrics and performance data. Proxy assignments start immediately and continue until you come back here and select Remove proxy."
          />
        )}
      </Text>
      <Text type='h200'>
        <FormattedMessage
          id='cs_perfportal_people_set_as_my_proxy'
          defaultMessage='People set as my proxy'
        />
      </Text>

      {userProfile.proxyCoaches.length === 0 ? (
        <Row
          alignmentVertical='center'
          spacing='200'
          widths={['grid-8', 'grid-4']}
        >
          <Text type='b300'>
            <FormattedMessage
              id='cs_perfportal_no_current_proxies'
              defaultMessage='None'
            />
          </Text>

          <Row alignmentHorizontal='end'>
            <Button type='primary' size='small' onClick={onAssignProxyForSelf}>
              <FormattedMessage
                id='cs_perfportal_assign_proxy_for_myself'
                defaultMessage='Assign a proxy for myself'
              />
            </Button>
          </Row>
        </Row>
      ) : (
        <Column spacing='300'>
          {userProfile.proxyCoaches.map(proxyCoach => (
            <Row
              key={proxyCoach.tmEntity.userID}
              alignmentVertical='center'
              spacing='200'
              widths={['grid-8', 'grid-4']}
            >
              <Text type='b300'>
                <FormattedMessage
                  id='cs_perfportal_user_assigned_as_my_proxy'
                  defaultMessage='{alias} is assigned as my proxy'
                  values={{ alias: proxyCoach.tmEntity.userName }}
                />
              </Text>

              <Row alignmentHorizontal='end'>
                <Button
                  type='tertiary'
                  size='small'
                  disabled={loadingProxyIds.has(proxyCoach.tmEntity.userID)}
                  onClick={() => {
                    if (onRemoveProxy) {
                      onRemoveProxy(proxyCoach.tmEntity.userID);
                    }
                  }}
                  minWidth={120}
                >
                  {loadingProxyIds.has(proxyCoach.tmEntity.userID) ? (
                    <Row alignmentVertical='center' spacing='200'>
                      <Loader size='small' />
                    </Row>
                  ) : (
                    <FormattedMessage
                      id='cs_perfportal_remove_proxy'
                      defaultMessage='Remove proxy'
                    />
                  )}
                </Button>
              </Row>
            </Row>
          ))}
        </Column>
      )}
      <Text type='h200'>
        <FormattedMessage
          id='cs_perfportal_people_who_have_set_me_as_proxy'
          defaultMessage='People who’ve set me as their proxy'
        />
      </Text>

      {peopleWhoSetMeAsProxy.length === 0 ? (
        <Box spacingInset='200 none'>
          <Text type='b300'>
            <FormattedMessage id='cs_perfportal_none' defaultMessage='None' />
          </Text>
        </Box>
      ) : (
        <Column spacing='300'>
          {peopleWhoSetMeAsProxy.map(proxyCoachee => (
            <Row
              key={proxyCoachee.tmEntity.userID}
              alignmentVertical='center'
              spacing='200'
              widths={['grid-8', 'grid-4']}
            >
              <Text type='b300'>
                <FormattedMessage
                  id='cs_perfportal_im_assigned_as_proxy'
                  defaultMessage="I'm assigned as proxy for {alias}"
                  values={{ alias: proxyCoachee.tmEntity.userName }}
                />
              </Text>
              <Row alignmentHorizontal='end'>
                {isRevampedProxyEnabled && (
                  <Button
                    type='primary'
                    size='small'
                    onClick={() =>
                      startProxySession(
                        `${cscEntityPrefix}:${proxyCoachee.tmEntity.userID}`,
                        proxyCoachee.tmEntity.userName
                      )
                    }
                    minWidth={140}
                  >
                    <FormattedMessage
                      id='cs_perfportal_start_proxy_session'
                      defaultMessage='Start proxy session'
                    />
                  </Button>
                )}
              </Row>
            </Row>
          ))}
        </Column>
      )}
      {showTMProxies && (
        <>
          <Box spacingInset='300 none none none'>
            <Text type='h200'>
              <FormattedMessage
                id='cs_perfportal_current_proxies_for_tms_i_manage'
                defaultMessage='Current proxies for TMs I manage'
              />
            </Text>
          </Box>

          {teamProxyState === DataState.LOADING ? (
            <Row
              alignmentHorizontal='center'
              spacingInset='400 none
            '
            >
              <Loader type='circular' size='large' />
            </Row>
          ) : (
            <>
              {!activeProxyRelationships.some(relation =>
                relation.proxyCoachees.some(coacheeId => {
                  return teamProfile?.reportingTeam.some(member => {
                    const memberFullId = `${cscEntityPrefix}:${member.relatedUser.userID}`;
                    return memberFullId === coacheeId && member.isManager;
                  });
                })
              ) ? (
                <Row
                  alignmentVertical='center'
                  spacing='200'
                  widths={['grid-8', 'grid-4']}
                >
                  <Text type='b300'>
                    <FormattedMessage
                      id='cs_perfportal_none'
                      defaultMessage='None'
                    />
                  </Text>

                  <Row alignmentHorizontal='end'>
                    <Button
                      type='primary'
                      size='small'
                      onClick={() => setStep(ProxyStep.ASSIGN_PROXY)}
                    >
                      <FormattedMessage
                        id='cs_perfportal_assign_proxy_for_tm'
                        defaultMessage='Assign proxy for a TM'
                      />
                    </Button>
                  </Row>
                </Row>
              ) : (
                <>
                  <Column spacing='300'>
                    {activeProxyRelationships.flatMap(
                      (relation: TeamProxyRelations) =>
                        relation.proxyCoachees.map((coacheeId: string) => {
                          const relationshipData = findProxyRelationshipData(
                            relation,
                            coacheeId,
                            teamProfile,
                            cscEntityPrefix
                          );

                          if (!relationshipData) {
                            return null;
                          }

                          const { proxyUserData, originalUserData, uniqueId } =
                            relationshipData;

                          return (
                            <Row
                              key={uniqueId}
                              alignmentVertical='center'
                              spacing='200'
                              widths={['grid-8', 'grid-4']}
                            >
                              <Text type='b300'>
                                <FormattedMessage
                                  id='cs_perfportal_user_assigned_as_proxy_for_tm'
                                  defaultMessage='{proxyAlias} is assigned as a proxy for {tmAlias}'
                                  values={{
                                    proxyAlias:
                                      proxyUserData.relatedUser.userName,
                                    tmAlias:
                                      originalUserData.relatedUser.userName,
                                  }}
                                />
                              </Text>

                              <Row alignmentHorizontal='end'>
                                <Button
                                  type='tertiary'
                                  size='small'
                                  disabled={loadingProxyIds.has(uniqueId)}
                                  onClick={() =>
                                    onRemoveOMProxy(
                                      proxyUserData.relatedUser.userID,
                                      originalUserData.relatedUser.userID,
                                      uniqueId,
                                      proxyUserData.relatedUser.userName,
                                      originalUserData.relatedUser.userName
                                    )
                                  }
                                  minWidth={120}
                                >
                                  {loadingProxyIds.has(uniqueId) ? (
                                    <Row
                                      alignmentVertical='center'
                                      spacing='200'
                                    >
                                      <Loader size='small' />
                                    </Row>
                                  ) : (
                                    <FormattedMessage
                                      id='cs_perfportal_remove_proxy'
                                      defaultMessage='Remove proxy'
                                    />
                                  )}
                                </Button>
                              </Row>
                            </Row>
                          );
                        })
                    )}
                  </Column>
                  <Box spacingInset='200 none 400 none'>
                    <Row alignmentHorizontal='end' widths='fit'>
                      <Button
                        type='primary'
                        size='small'
                        onClick={() => setStep(ProxyStep.ASSIGN_PROXY)}
                      >
                        <FormattedMessage
                          id='cs_perfportal_assign_proxy_for_tm'
                          defaultMessage='Assign proxy for a TM'
                        />
                      </Button>
                    </Row>
                  </Box>
                </>
              )}
            </>
          )}
        </>
      )}
      <ModalFooter>
        <Row alignmentHorizontal='end' widths='fit'>
          <Button type='secondary' onClick={onClose}>
            <FormattedMessage id='cs_perfportal_close' defaultMessage='Close' />
          </Button>
        </Row>
      </ModalFooter>
    </Column>
  );
};
